import { shownStatusKeys } from '../statusConfig/buildStatusTabs'

/**
 * Verdeelt een lijst records over de kolommen van het bord.
 *
 * Welke statussen een kolom krijgen is exact dezelfde regel als bij de
 * tabbladen boven de tabel — `shownStatusKeys` beslist dat voor allebei. Zouden
 * die twee los beslissen, dan toont de tabel een emmer die het bord niet heeft,
 * of andersom, en dat merk je pas als een record onvindbaar is.
 *
 * AI-CONTEXT: See docs/features/household-board-view.md
 * AI-CONTEXT: See docs/decisions.md#adr-032
 */

/**
 * @typedef {object} BoardColumn
 * @property {string} key - Statussleutel
 * @property {string} label - Kolomkop, in de woorden van de organisatie
 * @property {Array<object>} items - Alles op die status, in de volgorde van de invoer
 * @property {number} count - Hoeveel het er echt zijn — de kop toont dit, niet het aantal getoonde kaarten
 */

/**
 * @param {object} params
 * @param {Array<object>} [params.items] - De records
 * @param {object} [params.display] - Uitkomst van `resolveStatusDisplay`
 * @param {(item: object) => string} [params.getStatus] - Waar de status op een record staat
 * @param {(item: object) => number|null} [params.getSortValue] - Waarop een kolom oploopt; `null` gaat onderaan
 * @returns {Array<BoardColumn>}
 */
const groupItemsByStatus = ({
    items,
    display,
    getStatus = (item) => item?.Status,
    getSortValue,
}) => {
    const { labels = {} } = display || {}
    const lijst = Array.isArray(items) ? items : []

    // Eén pass: per statussleutel een emmer, en alles wat daar niet in past
    // valt buiten het bord. Bewust NIET in de eerste kolom laten vallen — dan
    // denkt de gebruiker dat het record daar staat, en overschrijft de eerste
    // sleep een status die niemand bedoeld heeft.
    const perStatus = {}
    lijst.forEach((item) => {
        const status = getStatus(item)
        if (!status) return
        if (!perStatus[status]) perStatus[status] = []
        perStatus[status].push(item)
    })

    const counts = Object.keys(perStatus).reduce((acc, key) => {
        acc[key] = perStatus[key].length
        return acc
    }, {})

    // Sorteren BINNEN een kolom is puur weergave: er wordt niets geschreven.
    // Wat wél buiten scope bleef is herordenen door slepen — dat schrijft
    // `Position` op elke rij van de lijst, bij de grootste organisatie 405
    // documenten per verplaatsing.
    //
    // Zonder sortering is de volgorde die van `Position`, de sleepvolgorde van
    // de tabel, en die is voor een planner willekeurig: een aanmelding van
    // augustus stond vóór een van juli. Records zonder waarde gaan onderaan,
    // zoals overal in deze codebase.
    const gesorteerd = (kolom) => {
        if (typeof getSortValue !== 'function') return kolom
        return [...kolom].sort((a, b) => {
            const va = getSortValue(a)
            const vb = getSortValue(b)
            if (va === null || va === undefined) return vb === null || vb === undefined ? 0 : 1
            if (vb === null || vb === undefined) return -1
            return va - vb
        })
    }

    return shownStatusKeys({ display, counts }).map((key) => ({
        key,
        label: labels[key],
        items: gesorteerd(perStatus[key] || []),
        count: (perStatus[key] || []).length,
    }))
}

export default groupItemsByStatus
